import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../components/Navbar';
import { updatedProductData, ProductData } from '../data/productData';
import { getStoreImagesForName } from '../data/storeImages';

// Updated Product interface to match new data structure
interface Product {
  id: number;
  name: string;
  slug: string;
  brand: string;
  price: string;
  originalPrice: string;
  newPrice: number;
  salePrice: number;
  condition: string;
  image: string;
  images: string[];
  category: string;
  badge: 'new' | 'sale' | 'limited' | null;
  inStock: boolean;
  isSecondHand?: boolean;
  size?: string;
}

// CURRENT PRODUCT-IMAGE MAPPING (PRESERVED - SAME AS ProductDetail.tsx):
/*
SHOES & CLOTHING mappings preserved exactly as in other files
*/

// Utility function to create URL-friendly slugs from product names
const createSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens and spaces
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

const generateProducts = (): Product[] => {
  const products: Product[] = [];
  
  // Convert updatedProductData to Product format
  updatedProductData.forEach((productData: ProductData) => {
    const storeImgs = getStoreImagesForName(productData.name, productData.category);
    const resolvedImages = storeImgs.length > 0 ? storeImgs : [`/images/products/${productData.category === 'Shoes' ? 'Ayakkabı' : 'Kıyafet'}/product${productData.id}.webp`];
    
    products.push({
      id: productData.id,
      name: productData.name,
      slug: productData.slug,
      brand: productData.brand,
      price: `₺${productData.salePrice.toLocaleString()}`,
      originalPrice: productData.newPrice > 0 ? `$${productData.newPrice.toLocaleString()}` : '',
      newPrice: productData.newPrice,
      salePrice: productData.salePrice,
      condition: productData.condition,
      image: resolvedImages[0],
      images: resolvedImages,
      category: productData.category,
      badge: Math.random() > 0.7 ? 'new' : Math.random() > 0.8 ? 'sale' : null,
      inStock: Math.random() > 0.1,
      isSecondHand: productData.isSecondHand,
      size: productData.size
    });
  });
  
  return products;
};

// Store Navigation Bar
const NavigationBar = styled.nav`
  position: relative;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid #f5f5f5;
  padding: 1rem 0;
  transition: all 0.3s ease;
  margin-top: 0;
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 300;
  letter-spacing: -0.02em;
  color: #1a1a1a;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: #d4af37;
    transform: scale(1.05);
  }
`;

const NavCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex: 1;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    width: 100%;
  }
`;

const SearchBar = styled.div`
  position: relative;
  width: 300px;
  
  @media (max-width: 1024px) {
    width: 200px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 25px;
  background: #f5f5f5;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
  
  &::placeholder {
  color: #888888;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  background-image: url('/images/logos/search.webp');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  filter: brightness(0.5);
`;

const CategoryDropdown = styled.div`
  position: relative;
`;

const CategoryDropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 200px;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  margin-top: 0.5rem;
`;

const CategoryDropdownItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  color: #1a1a1a;
  
  &:hover {
    background: #f5f5f5;
  }
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const CategoryDropdownButton = styled.button`
  background: none;
  border: none;
  color: #1a1a1a;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const MyOrdersButton = styled.button`
  padding: 0.5rem 1.5rem;
  background: transparent;
  border: none;
  outline: none;
  color: #000000;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
  }
  
  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.75rem;
  }
`;

const CartButton = styled.button`
  position: relative;
  background: none;
  border: none;
  outline: none;
  color: #1a1a1a;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  
  &:focus {
    outline: none;
  }
  transition: all 0.3s ease;
  width: 24px;
  height: 24px;
  background-image: url('/images/logos/pngwing.com (11).webp');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const CartBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background: #d4af37;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Styled Components
const AllProductsContainer = styled.div`
  min-height: 100vh;
  background: #0d0d0d;
  color: #ffffff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  padding-top: 0;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageHeader = styled.div`
  padding: 2rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  margin-bottom: 2rem;
`;

const Breadcrumb = styled.div`
  font-size: 0.875rem;
  color: #cfcfcf;
  margin-bottom: 1rem;
  
  a {
    color: #d4af37;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #ffffff;
  font-family: serif;
`;

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: #cfcfcf;
  font-weight: 300;
`;

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 2rem;
  min-height: calc(100vh - 200px);
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FiltersSidebar = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 1.5rem;
  height: fit-content;
  position: sticky;
  top: 2rem;
  
  @media (max-width: 1024px) {
    position: static;
    order: 1;
  }
`;

const FilterSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FilterCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #cfcfcf;
  
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #d4af37;
  }
`;

const FilterCount = styled.span`
  color: #888888;
  font-size: 0.75rem;
  margin-left: auto;
`;

const ProductsSection = styled.div`
  flex: 1;
  
  @media (max-width: 1024px) {
    order: 2;
  }
`;

const ProductsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const ResultsCount = styled.p`
  font-size: 0.875rem;
  color: #cfcfcf;
  margin: 0;
`;

const SortSelect = styled.select`
  background: #1a1a1a;
  color: #ffffff;
  border: 1px solid #333333;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled(motion.div)`
  background: #111111;
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.26s cubic-bezier(0.22, 1, 0.36, 1);
  cursor: pointer;
  position: relative;
  
  &:hover {
    border-color: #d4af37;
    transform: translateY(-6px);
    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.6);
  }
`;

const ProductImageContainer = styled.div`
  position: relative;
  aspect-ratio: 3248 / 4330; /* 3:4 portrait to match source images */
  overflow: hidden;
  background: #0f0f0f;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover; /* fill without side gaps, keep ratio */
  object-position: center;
  transition: transform 0.26s cubic-bezier(0.22, 1, 0.36, 1);
  
  ${ProductCard}:hover & {
    transform: scale(1.02);
  }
`;

const ProductBadge = styled.div<{ type: 'new' | 'sale' | 'limited' }>`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: ${props => 
    props.type === 'new' ? '#00b894' : 
    props.type === 'sale' ? '#e74c3c' : 
    '#d4af37'
  };
  color: #ffffff;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ProductInfo = styled.div`
  padding: 1.25rem;
`;

const ProductBrand = styled.div`
  font-size: 0.75rem;
  color: #888888;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.5rem;
`;

const ProductName = styled.h3`
  font-size: 1rem;
  font-weight: 400;
  margin-bottom: 0.5rem;
  color: #ffffff;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ProductPrice = styled.div`
  font-size: 1.125rem;
  font-weight: 300;
  color: #d4af37;
  margin-bottom: 0.5rem;
`;

const ProductOriginalPrice = styled.div`
  font-size: 0.875rem;
  color: #888888;
  margin-bottom: 0.5rem;
`;

const ProductCondition = styled.div`
  font-size: 0.75rem;
  color: #d4af37;
  font-weight: 500;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ProductActions = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.7);
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${ProductCard}:hover & {
    opacity: 1;
  }
`;

const AddToCartButton = styled.button`
  background: #d4af37;
  color: #0d0d0d;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  margin-right: 0.5rem;
  
  &:hover {
    background: #b8941f;
    transform: translateY(-2px);
  }
  
  &.added {
    background: #1a1a1a;
    color: #d4af37;
    pointer-events: none;
  }
`;


const WishlistButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;


const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #cfcfcf;
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #ffffff;
`;

const EmptyStateText = styled.p`
  font-size: 1rem;
  margin-bottom: 2rem;
  color: #cfcfcf;
`;

const ClearFiltersButton = styled.button`
  background: #d4af37;
  color: #0d0d0d;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #b8941f;
    transform: translateY(-1px);
  }
`;

const AllProducts: React.FC = () => {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const [allProducts] = useState<Product[]>(generateProducts());
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(allProducts);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedCondition, setSelectedCondition] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>(() => {
    return localStorage.getItem('productSortBy') || 'relevance';
  });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [navSearchQuery, setNavSearchQuery] = useState<string>('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [addedToCartId, setAddedToCartId] = useState<number | null>(null);
  const [soldProductIds, setSoldProductIds] = useState<number[]>([]);
  const [reservedProductIds, setReservedProductIds] = useState<number[]>([]);
  
  // Sepet bağlamını al
  const { addToCart, cartCount, loading: cartLoading } = useCart();

  // Satılan ve rezerve ürünleri getir
  useEffect(() => {
    const fetchSoldProducts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/sold-products`);
        const data = await response.json();
        if (data.success) {
          setSoldProductIds(data.sold_product_ids);
        }
      } catch (error) {
        // Hata sessizce yoksayılır
      }
    };
    
    const fetchReservedProducts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/reserved-products`);
        const data = await response.json();
        if (data.success) {
          setReservedProductIds(data.reserved_product_ids);
        }
      } catch (error) {
        // Hata sessizce yoksayılır
      }
    };
    
    fetchSoldProducts();
    fetchReservedProducts();
  }, []);

  // Save sortBy to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('productSortBy', sortBy);
  }, [sortBy]);

  // Filter products based on selected filters
  useEffect(() => {
    let filtered = [...allProducts];

    // Satılan ürünleri filtrele (kargoya verilmiş veya teslim edilmiş)
    filtered = filtered.filter(product => !soldProductIds.includes(product.id));

    // Category filter
    if (selectedCategory !== 'all') {
      if (selectedCategory === 'Clothing') {
        // Clothing seçildiğinde tüm giyim kategorilerini göster
        filtered = filtered.filter(product => 
          product.category === 'Clothing' || 
          product.category === 'Jackets' || 
          product.category === 'Shirts' || 
          product.category === 'T-Shirts' || 
          product.category === 'Special'
        );
      } else if (selectedCategory === 'Shoes' || selectedCategory === 'Jackets' || selectedCategory === 'Shirts' || selectedCategory === 'T-Shirts' || selectedCategory === 'Special') {
        // Diğer ana kategoriler
        filtered = filtered.filter(product => product.category === selectedCategory);
      } else if (selectedCategory === 'Kemer' || selectedCategory === 'Saatler' || selectedCategory === 'Kravat' || selectedCategory === 'Güneş gözlüğü') {
        // Coming soon kategoriler - boş array döndür
        filtered = [];
      }
    }

    // Condition filter
    if (selectedCondition !== 'all') {
      if (selectedCondition === 'secondhand') {
        filtered = filtered.filter(product => product.isSecondHand);
      } else if (selectedCondition === 'new') {
        filtered = filtered.filter(product => !product.isSecondHand);
      }
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.brand.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort products
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => {
          const priceA = parseInt(a.price.replace(/[^\d]/g, ''));
          const priceB = parseInt(b.price.replace(/[^\d]/g, ''));
          return priceA - priceB;
        });
        break;
      case 'price-high':
        filtered.sort((a, b) => {
          const priceA = parseInt(a.price.replace(/[^\d]/g, ''));
          const priceB = parseInt(b.price.replace(/[^\d]/g, ''));
          return priceB - priceA;
        });
        break;
      case 'newest':
        // Kondisyona göre sırala: Like New > Very Good > Good > Old but Good
        const conditionOrder: { [key: string]: number } = {
          'Like New': 4,
          'Very Good': 3,
          'Good': 2,
          'Old but Good': 1
        };
        
        filtered.sort((a, b) => {
          const conditionA = conditionOrder[a.condition] || 0;
          const conditionB = conditionOrder[b.condition] || 0;
          
          // Önce kondisyona göre sırala (en iyi üstte)
          if (conditionB !== conditionA) {
            return conditionB - conditionA;
          }
          
          // Aynı kondisyonsa ID'ye göre sırala (yeni üstte)
          return b.id - a.id;
        });
        break;
      default:
        // İlk 12 ürünü (2 sıra) en pahalılardan rastgele seç
        const sortedByPrice = [...filtered].sort((a, b) => {
          const priceA = parseInt(a.price.replace(/[^\d]/g, ''));
          const priceB = parseInt(b.price.replace(/[^\d]/g, ''));
          return priceB - priceA;
        });
        
        // En pahalı 20 üründen rastgele 12 seç
        const topExpensive = sortedByPrice.slice(0, Math.min(20, sortedByPrice.length));
        const shuffled = [...topExpensive].sort(() => Math.random() - 0.5);
        const topProducts = shuffled.slice(0, 12);
        
        // Geri kalan ürünler
        const remainingProducts = filtered.filter(p => !topProducts.find(tp => tp.id === p.id));
        
        // Birleştir: İlk 12 pahalı (rastgele), sonra geri kalanlar
        filtered = [...topProducts, ...remainingProducts];
        break;
    }

    setFilteredProducts(filtered);
  }, [allProducts, selectedCategory, selectedCondition, sortBy, searchQuery, soldProductIds]);

  const handleProductClick = (product: any) => {
    const category = product.category as string | undefined;
    const categorySlug = category && category.toLowerCase() === 'shoes' ? 'shoes' : category && category.toLowerCase() === 'clothing' ? 'clothing' : undefined;
    
    if (categorySlug) {
      navigate(`/store/${categorySlug}/${product.slug}`);
    } else {
      navigate(`/store/${product.slug}`);
    }
  };

  // Handle navigation search input change
  const handleNavSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNavSearchQuery(e.target.value);
  };

  // Handle category dropdown toggle
  const toggleCategoryDropdown = () => {
    setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
  };

  // Handle category selection from nav dropdown
  const handleNavCategorySelect = (category: string) => {
    setIsCategoryDropdownOpen(false);
    if (category === 'all') {
      setSelectedCategory('all');
    } else {
      setSelectedCategory(category);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedCondition('all');
    setSortBy('relevance');
    setSearchQuery('');
  };

  const handleAddToCart = (product: Product, e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart(product);
    setAddedToCartId(product.id);
    
    // 2 saniye sonra animasyonu kaldır
    setTimeout(() => {
      setAddedToCartId(null);
    }, 2000);
  };

  const categories = [
    { id: 'all', name: language === 'tr' ? 'Tüm Kategoriler' : 'All Categories', count: allProducts.length },
    { id: 'Clothing', name: language === 'tr' ? 'Giyim' : 'Clothing', count: allProducts.filter(p => p.category === 'Clothing' || p.category === 'Jackets' || p.category === 'Shirts' || p.category === 'T-Shirts' || p.category === 'Special').length },
    { id: 'Jackets', name: language === 'tr' ? 'Ceketler' : 'Jackets', count: allProducts.filter(p => p.category === 'Jackets').length },
    { id: 'Shirts', name: language === 'tr' ? 'Gömlekler' : 'Shirts', count: allProducts.filter(p => p.category === 'Shirts').length },
    { id: 'T-Shirts', name: language === 'tr' ? 'Tişörtler & Pololar' : 'T-Shirts & Polos', count: allProducts.filter(p => p.category === 'T-Shirts').length },
    { id: 'Special', name: language === 'tr' ? 'Özel Setler' : 'Special Sets', count: allProducts.filter(p => p.category === 'Special').length },
    { id: 'Shoes', name: language === 'tr' ? 'Ayakkabılar' : 'Shoes', count: allProducts.filter(p => p.category === 'Shoes').length },
    { id: 'Kemer', name: language === 'tr' ? 'Kemer' : 'Belts', count: 0 },
    { id: 'Saatler', name: language === 'tr' ? 'Saatler' : 'Watches', count: 0 },
    { id: 'Kravat', name: language === 'tr' ? 'Kravat' : 'Ties', count: 0 },
    { id: 'Güneş gözlüğü', name: language === 'tr' ? 'Güneş Gözlüğü' : 'Sunglasses', count: 0 }
  ];

  const conditions = [
    { id: 'all', name: language === 'tr' ? 'Tüm Durumlar' : 'All Conditions', count: allProducts.length },
    { id: 'new', name: language === 'tr' ? 'Yeni' : 'New', count: allProducts.filter(p => !p.isSecondHand).length },
    { id: 'secondhand', name: language === 'tr' ? 'İkinci El' : 'Second-Hand', count: allProducts.filter(p => p.isSecondHand).length }
  ];

  return (
    <>
      <Helmet>
        <title>{language === 'tr' ? 'Tüm Ürünler - Mağaza' : 'All Products - Store'}</title>
        <meta name="description" content={language === 'tr' ? 'Lüks moda ürünlerimizin tüm koleksiyonuna göz atın' : 'Browse our complete collection of luxury fashion items'} />
      </Helmet>
      
      <AllProductsContainer>
        {/* Store Navigation */}
        <NavigationBar>
          <NavContainer>
            <Logo onClick={() => navigate('/store')}>STORE</Logo>
            
            <NavCenter>
              <SearchBar>
                <SearchIcon></SearchIcon>
                <SearchInput
                  type="text"
                  placeholder="Search items..."
                  value={navSearchQuery}
                  onChange={handleNavSearchInputChange}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      setSearchQuery(navSearchQuery);
                    }
                  }}
                />
              </SearchBar>
            </NavCenter>
            
            <NavRight>
              <MyOrdersButton onClick={() => navigate('/')}>
                {language === 'tr' ? 'Ana Sayfa' : 'Home'}
              </MyOrdersButton>
              <MyOrdersButton onClick={() => navigate('/orders')}>
                {language === 'tr' ? 'Siparişlerim' : 'My Orders'}
              </MyOrdersButton>
              <div style={{ position: 'relative' }}>
                <MyOrdersButton onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}>
                  🌐 {language.toUpperCase()}
                </MyOrdersButton>
                {isLanguageDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    background: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                    minWidth: '140px',
                    zIndex: 1000
                  }}>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setLanguage('tr'); setIsLanguageDropdownOpen(false); }} 
                      style={{
                        width: '100%',
                        padding: '12px 20px',
                        border: 'none',
                        background: language === 'tr' ? '#d4af37' : 'white',
                        color: language === 'tr' ? 'white' : '#1a1a1a',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: language === 'tr' ? '600' : '400',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => { if (language !== 'tr') e.currentTarget.style.background = '#f5f5f5'; }}
                      onMouseLeave={(e) => { if (language !== 'tr') e.currentTarget.style.background = 'white'; }}
                    >
                      🇹🇷 Türkçe
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setLanguage('en'); setIsLanguageDropdownOpen(false); }} 
                      style={{
                        width: '100%',
                        padding: '12px 20px',
                        border: 'none',
                        background: language === 'en' ? '#d4af37' : 'white',
                        color: language === 'en' ? 'white' : '#1a1a1a',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: language === 'en' ? '600' : '400',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => { if (language !== 'en') e.currentTarget.style.background = '#f5f5f5'; }}
                      onMouseLeave={(e) => { if (language !== 'en') e.currentTarget.style.background = 'white'; }}
                    >
                      🇬🇧 English
                    </button>
                  </div>
                )}
              </div>
              <CartButton onClick={() => navigate('/cart')}>
                {cartCount > 0 && <CartBadge>{cartCount}</CartBadge>}
                {cartLoading && <CartBadge>...</CartBadge>}
              </CartButton>
            </NavRight>
          </NavContainer>
        </NavigationBar>
        
        <Container>
          <PageHeader>
            <Breadcrumb>
              <a href="/store">{language === 'tr' ? 'Mağaza' : 'Store'}</a> / {language === 'tr' ? 'Tüm Ürünler' : 'All Products'}
            </Breadcrumb>
            <PageTitle>{language === 'tr' ? 'Tüm Ürünler' : 'All Products'}</PageTitle>
            <PageSubtitle>
              {language === 'tr' ? 'Lüks moda ürünlerimizin tüm koleksiyonunu keşfedin' : 'Discover our complete collection of luxury fashion items'}
            </PageSubtitle>
          </PageHeader>

          <MainLayout>
            <FiltersSidebar>
              <FilterSection>
                <FilterTitle>{language === 'tr' ? 'Ara' : 'Search'}</FilterTitle>
                <input
                  type="text"
                  placeholder={language === 'tr' ? 'Ürün ara...' : 'Search products...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#111111',
                    border: '1px solid #333333',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '0.875rem'
                  }}
                />
              </FilterSection>

              <FilterSection>
                <FilterTitle>{language === 'tr' ? 'Kategori' : 'Category'}</FilterTitle>
                <FilterGroup>
                  {categories.map(category => (
                    <FilterCheckbox 
                      key={category.id}
                      style={{ 
                        opacity: category.count === 0 ? 0.5 : 1,
                        cursor: category.count === 0 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <input
                        type="radio"
                        name="category"
                        value={category.id}
                        checked={selectedCategory === category.id}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        disabled={category.count === 0}
                      />
                      <span>{category.name}</span>
                      <FilterCount>
                        {category.count === 0 ? (language === 'tr' ? 'Yakında' : 'Coming Soon') : `(${category.count})`}
                      </FilterCount>
                    </FilterCheckbox>
                  ))}
                </FilterGroup>
              </FilterSection>

              <FilterSection>
                <FilterTitle>{language === 'tr' ? 'Durum' : 'Condition'}</FilterTitle>
                <FilterGroup>
                  {conditions.map(condition => (
                    <FilterCheckbox key={condition.id}>
                      <input
                        type="radio"
                        name="condition"
                        value={condition.id}
                        checked={selectedCondition === condition.id}
                        onChange={(e) => setSelectedCondition(e.target.value)}
                      />
                      <span>{condition.name}</span>
                      <FilterCount>({condition.count})</FilterCount>
                    </FilterCheckbox>
                  ))}
                </FilterGroup>
              </FilterSection>
            </FiltersSidebar>

            <ProductsSection>
              <ProductsHeader>
                <ResultsCount>
                  {filteredProducts.length} {language === 'tr' ? 'ürün bulundu' : 'products found'}
                </ResultsCount>
                <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="relevance">{language === 'tr' ? 'İlgiye Göre Sırala' : 'Sort by Relevance'}</option>
                  <option value="price-low">{language === 'tr' ? 'Fiyat: Düşükten Yüksek' : 'Price: Low to High'}</option>
                  <option value="price-high">{language === 'tr' ? 'Fiyat: Yüksekten Düşüğe' : 'Price: High to Low'}</option>
                  <option value="newest">{language === 'tr' ? 'En Yeniler' : 'Newest First'}</option>
                </SortSelect>
              </ProductsHeader>

              {filteredProducts.length === 0 ? (
                <EmptyState>
                  <EmptyStateTitle>{language === 'tr' ? 'Ürün bulunamadı' : 'No products found'}</EmptyStateTitle>
                  <EmptyStateText>
                    {language === 'tr' ? 'Filtrelerinizi veya arama terimlerinizi ayarlamayı deneyin' : 'Try adjusting your filters or search terms'}
                  </EmptyStateText>
                  <ClearFiltersButton onClick={clearFilters}>
                    {language === 'tr' ? 'Tüm Filtreleri Temizle' : 'Clear All Filters'}
                  </ClearFiltersButton>
                </EmptyState>
              ) : (
                <ProductsGrid>
                    {filteredProducts.map((product, index) => (
                      <ProductCard
                        key={product.id}
                        onClick={() => handleProductClick(product)}
                      >
                        <ProductImageContainer>
                          <ProductImage 
                            src={product.image} 
                            alt={product.name}
                            loading="lazy"
                          />
                          {reservedProductIds.includes(product.id) && (
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%) rotate(-15deg)',
                              fontSize: '1.5rem',
                              fontWeight: 'bold',
                              color: '#fff',
                              textTransform: 'uppercase',
                              letterSpacing: '3px',
                              textShadow: '0 0 15px rgba(0,0,0,0.8), 0 0 30px rgba(0,0,0,0.6)',
                              zIndex: 2,
                              pointerEvents: 'none'
                            }}>
                              {language === 'tr' ? 'REZERVELİ' : 'RESERVED'}
                            </div>
                          )}
                          <ProductActions>
                            <AddToCartButton 
                              className={addedToCartId === product.id ? 'added' : ''}
                              onClick={(e: React.MouseEvent) => handleAddToCart(product, e)}
                              disabled={reservedProductIds.includes(product.id)}
                              style={{
                                opacity: reservedProductIds.includes(product.id) ? 0.5 : 1,
                                cursor: reservedProductIds.includes(product.id) ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {reservedProductIds.includes(product.id) 
                                ? (language === 'tr' ? 'Rezerveli' : 'Reserved') 
                                : addedToCartId === product.id 
                                ? (language === 'tr' ? 'Sepete Eklendi' : 'Added to Cart') 
                                : cartLoading 
                                ? (language === 'tr' ? 'Ekleniyor...' : 'Adding...') 
                                : (language === 'tr' ? 'Sepete Ekle' : 'Add to Cart')}
                            </AddToCartButton>
                          </ProductActions>
                        </ProductImageContainer>
                        
                        <ProductInfo>
                          <ProductBrand>{product.brand}</ProductBrand>
                          <ProductName>{product.name}</ProductName>
                          <ProductPrice>{product.price} - {language === 'tr' ? 'Satış Fiyatı' : 'Sale Price'}</ProductPrice>
                          {product.originalPrice && product.originalPrice !== product.price && (
                            <ProductOriginalPrice>{product.originalPrice} - {language === 'tr' ? 'Mağaza Fiyatı' : 'Store Price'}</ProductOriginalPrice>
                          )}
                          <ProductCondition>{product.condition}</ProductCondition>
                        </ProductInfo>
                      </ProductCard>
                    ))}
                </ProductsGrid>
              )}
            </ProductsSection>
          </MainLayout>
        </Container>
      </AllProductsContainer>
    </>
  );
};

export default AllProducts;
